import { isoToDate, type IsoDate } from './isoDate';

/**
 * WHICH MONTHS THE CALENDAR SHOWS — the platform calendar spec's *Navigation* and *Default
 * panel positions*, as plain arithmetic the fields and the grid share.
 *
 * A month is one integer, `year * 12 + month` (month 0-based), so "a year back" is `- 12`
 * and "the right panel stays after the left" is `left < right`, with no `Date` rollover to
 * get wrong. A range calendar has two panels that move INDEPENDENTLY: an arrow, a month or
 * year pick, or the keyboard moves one panel; the other only moves when it would otherwise
 * show the same month or an earlier one ("no blocking" — it is pushed along by one month).
 */

/** `year * 12 + month`, month 0-based. */
export type MonthIndex = number;

export const toMonthIndex = (date: Date): MonthIndex => date.getFullYear() * 12 + date.getMonth();

export const fromMonthIndex = (month: MonthIndex): Date =>
  new Date(Math.floor(month / 12), ((month % 12) + 12) % 12, 1);

/** The month a day falls in, or `null` for no (or no real) day. */
export const isoMonth = (iso: IsoDate | null | undefined): MonthIndex | null => {
  const date = isoToDate(iso);
  return date ? toMonthIndex(date) : null;
};

/** The months a panel may show; `null` is unbounded. */
export interface MonthLimits {
  min: MonthIndex | null;
  max: MonthIndex | null;
}

export const NO_LIMITS: MonthLimits = { min: null, max: null };

/** The calendar's day bounds as months. */
export function monthLimits(minDate?: IsoDate | null, maxDate?: IsoDate | null): MonthLimits {
  return { min: isoMonth(minDate), max: isoMonth(maxDate) };
}

/**
 * The months ONE panel may show. With two panels the left stops a month before the last
 * allowed month and the right a month after the first, so the pair always fits inside the
 * bounds with the right one later. A window narrower than two months still shows two
 * panels — the days outside it are simply not pickable.
 */
export function panelLimits(index: number, count: number, limits: MonthLimits): MonthLimits {
  if (count === 1) return limits;
  const { min, max } = limits;
  if (index === 0) {
    const leftMax = max === null ? null : min !== null ? Math.max(min, max - 1) : max - 1;
    return { min, max: leftMax };
  }
  const rightMin = min === null ? null : min + 1;
  const rightMax = max === null ? null : rightMin !== null ? Math.max(rightMin, max) : max;
  return { min: rightMin, max: rightMax };
}

export function clampMonth(month: MonthIndex, limits: MonthLimits): MonthIndex {
  if (limits.min !== null && month < limits.min) return limits.min;
  if (limits.max !== null && month > limits.max) return limits.max;
  return month;
}

export function withinLimits(month: MonthIndex, limits: MonthLimits): boolean {
  return clampMonth(month, limits) === month;
}

/**
 * Panel `index` moves to `target` (clamped to what it may show). The other panel stays —
 * unless the move would leave the right panel on or before the left one, in which case
 * the other panel is pushed along so the right one is exactly one month later.
 */
export function movePanel(
  panels: readonly MonthIndex[],
  index: number,
  target: MonthIndex,
  limits: MonthLimits = NO_LIMITS,
): MonthIndex[] {
  const next = [...panels];
  next[index] = clampMonth(target, panelLimits(index, panels.length, limits));
  if (next.length === 2 && next[0] >= next[1]) {
    if (index === 0) next[1] = next[0] + 1;
    else next[0] = next[1] - 1;
  }
  return next;
}

export interface OpeningInput {
  /** 1 for a single date, 2 for a range. */
  count: 1 | 2;
  start: IsoDate | null;
  /** Ignored for a single date. */
  end?: IsoDate | null;
  /** The range is open-ended: it has a start and deliberately no end. */
  openEnded?: boolean;
  /**
   * The month the FEATURE proposes when nothing is set (spec rule 2) — the correction
   * journal's financial-year end, the period picker's neighbouring year. A range opens on
   * it and the eleventh month after it, the same year-wide shape as rule 3.
   */
  referenceDate?: IsoDate | null;
  limits?: MonthLimits;
  today?: Date;
}

/**
 * Where the calendar opens, before anyone clicks — the spec's first rule that applies:
 *
 * 1. **Dates already set:** the left panel shows the start's month, the right the end's;
 *    both in one month → the right shows the next month. An open end puts the right panel
 *    on the current month. Only an end → the left panel is the month before it.
 * 2. **The feature proposes a month** (`referenceDate`): that month, and (range) the
 *    eleventh after it.
 * 3. **Nothing set:** January and December of the current year (a single date: the
 *    current month).
 *
 * Whatever comes out is clamped into the bounds.
 */
export function openingPanels({
  count,
  start,
  end = null,
  openEnded = false,
  referenceDate,
  limits = NO_LIMITS,
  today = new Date(),
}: OpeningInput): MonthIndex[] {
  const current = toMonthIndex(today);
  const startMonth = isoMonth(start);
  const reference = isoMonth(referenceDate);

  if (count === 1) {
    return [clampMonth(startMonth ?? reference ?? current, limits)];
  }

  const endMonth = isoMonth(end);
  let left: MonthIndex;
  let right: MonthIndex;
  if (startMonth !== null || endMonth !== null) {
    left = startMonth ?? (endMonth as MonthIndex) - 1;
    right = endMonth ?? (openEnded ? current : left + 1);
  } else if (reference !== null) {
    left = reference;
    right = reference + 11;
  } else {
    left = today.getFullYear() * 12;
    right = left + 11;
  }
  if (right <= left) right = left + 1;

  left = clampMonth(left, panelLimits(0, 2, limits));
  right = clampMonth(right, panelLimits(1, 2, limits));
  return right > left ? [left, right] : movePanel([left, right], 0, left, limits);
}

/**
 * A whole day typed into a field moves ITS panel to that day's month: the start (or the
 * single date) the left panel, the end the right one.
 */
export function followTypedDay(
  panels: readonly MonthIndex[],
  bound: 'start' | 'end',
  day: IsoDate,
  limits: MonthLimits = NO_LIMITS,
): MonthIndex[] {
  const month = isoMonth(day);
  if (month === null) return [...panels];
  const index = panels.length === 1 || bound === 'start' ? 0 : 1;
  return panels[index] === month ? [...panels] : movePanel(panels, index, month, limits);
}
